import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { RewardsCard } from "@/components/dashboard/RewardsCard";
import { Card, CardContent } from "@/components/ui/card";
import { type RewardData } from "@/components/referrals/tableConfigs";
import { RewardStatus } from "@/lib/schema";
import { RedeemModal } from "@/components/rewards/RedeemModal";
import { RewardsCardSkeleton } from "@/components/rewards/RewardsCardSkeleton";
import { RewardsTable } from "@/components/rewards/RewardsTable";
import { getUserRewards } from "@/api/stats";

// Import custom SVG icons
import iconRewards from "@/assets/rewards/icon_rewards.svg";
import iconPaid from "@/assets/rewards/icon_paid.svg";
import iconPending from "@/assets/rewards/icon_pending.svg";


export default function Rewards() {
  const { userData } = useAuth();
  const [selectedReward, setSelectedReward] = useState<RewardData | null>(null);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);

  const { data: rewards = [], isLoading } = useQuery<RewardData[]>({
    queryKey: ["rewards", userData?.id],
    queryFn: () => getUserRewards(userData!.id),
    enabled: !!userData?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const handleRedeemClick = (reward: RewardData) => {
    setSelectedReward(reward);
    setIsRedeemModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsRedeemModalOpen(false);
    setSelectedReward(null);
  };

  const handleRedeem = (rewardId: string) => {
    // TODO: Implement actual redeem logic here
    console.log('Redeeming reward:', rewardId);
    // You can add API call here to redeem the reward
  };


  const displayRewards = rewards;

  // Calculate totals
  const totalRewards = displayRewards.reduce((sum, reward) => {
    return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
  }, 0);

  const paidRewards = displayRewards
    .filter(reward => reward.status === RewardStatus.PAID)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);

  const pendingRewards = displayRewards
    .filter(reward => reward.status === RewardStatus.PENDING)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);


  return (
    <ProtectedRoute>
      <div className="min-h-screen page-gradient">
        <Sidebar />
        <div className="lg:ml-56 p-4 lg:p-8 pt-20 lg:pt-8">
          {/* Header */}
          <DashboardHeader 
            title="Rewards"
            subtitle="Earn up to R20 000 in rewards for client or candidate referrals!"
            className="mb-6 lg:mb-8"
          />

          {/* Reward Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
            {isLoading ? (
              <>
                <RewardsCardSkeleton />
                <RewardsCardSkeleton />
                <RewardsCardSkeleton />
              </>
            ) : (
              <>
                <RewardsCard
                  title="TOTAL REWARDS"
                  value={totalRewards}
                  icon={iconRewards}
                  variant="dark"
                  testId="total-rewards-card"
                />
                
                <RewardsCard
                  title="PAID"
                  value={paidRewards}
                  icon={iconPaid}
                  variant="default"
                  testId="paid-rewards-card"
                />
                
                <RewardsCard
                  title="PENDING"
                  value={pendingRewards}
                  icon={iconPending}
                  variant="default"
                  testId="pending-rewards-card"
                />
              </>
            )}
          </div>

          {/* Store Redemption Info */}
          <Card className="border border-gray-200 mb-6 lg:mb-8">
            <CardContent className="p-4 min-[375px]:p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 mb-4 min-[375px]:mb-5 sm:mb-6">
                <div className="flex-shrink-0 mx-auto sm:mx-0 mb-3 sm:mb-0">
                  <div className="w-12 h-12 min-[375px]:w-14 min-[375px]:h-14 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 min-[375px]:w-7 min-[375px]:h-7 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <h3 className="text-xl min-[375px]:text-2xl sm:text-xl font-semibold font-space-grotesk text-gray-900 mb-2 min-[375px]:mb-3 sm:mb-2 leading-tight">
                    Redeem at Your Favourite Stores
                  </h3>
                  <p className="text-base min-[375px]:text-lg sm:text-base text-gray-600 leading-relaxed">
                    Your rewards can be used at these popular retailers across South Africa
                  </p>
                </div>
              </div>

              {/* Store Grid */}
              <div className="grid grid-cols-2 min-[375px]:grid-cols-2 min-[425px]:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 min-[375px]:gap-3 sm:gap-4">
                {[
                  { name: "iStore", available: true },
                  { name: "HiFi Corp", available: true },
                  { name: "Vida e Caffé", available: true },
                  { name: "Sportsmans Warehouse", available: true },
                  { name: "Cape Union Mart", available: true },
                  { name: "Clicks", available: true },
                  { name: "Kauai", available: true },
                  { name: "Sunglass Hut", available: true },
                  { name: "Nike", available: true },
                  { name: "Takealot", available: false }
                ].map((store, index) => (
                  <div
                    key={index}
                    className={`
                      relative group px-2 py-2.5 min-[375px]:px-3 min-[375px]:py-3 sm:px-4 sm:py-3 
                      rounded-lg min-[375px]:rounded-xl text-center font-medium 
                      transition-all duration-300 transform active:scale-95 
                      min-h-[44px] flex items-center justify-center
                      ${store.available 
                        ? 'bg-white border-2 border-gray-100 text-gray-800 hover:border-orange-200 hover:shadow-lg sm:hover:scale-105 hover:bg-gradient-to-br hover:from-white hover:to-orange-50' 
                        : 'bg-gray-50 border-2 border-gray-100 text-gray-400'
                      }
                    `}
                  >
                    <span className="block truncate text-xs min-[375px]:text-sm font-space-grotesk font-semibold tracking-wide leading-tight">
                      {store.name}
                    </span>
                    {store.available && (
                      <div className="absolute inset-0 rounded-lg min-[375px]:rounded-xl bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                    {!store.available && (
                      <div className="absolute -top-1.5 -right-1.5 min-[375px]:-top-2 min-[375px]:-right-2">
                        <span className="inline-flex items-center px-1.5 py-0.5 min-[375px]:px-2 min-[375px]:py-1 rounded-full text-xs font-semibold font-space-grotesk bg-gradient-to-r from-orange-400 to-red-400 text-white shadow-lg">
                          Soon
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-4 min-[375px]:mt-5 sm:mt-6 pt-4 min-[375px]:pt-5 sm:pt-6 border-t border-gray-200">
                <p className="text-xs min-[375px]:text-sm text-gray-500 text-center leading-relaxed">
                  More stores being added regularly • Rewards never expire
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Rewards Table */}
          <RewardsTable
            rewards={displayRewards}
            onRedeemClick={handleRedeemClick}
            itemsPerPage={10}
            showPagination={true}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Redeem Modal */}
      <RedeemModal
        isOpen={isRedeemModalOpen}
        onClose={handleCloseModal}
        reward={selectedReward}
        onRedeem={handleRedeem}
      />
    </ProtectedRoute>
  );
}